import { getEntityDisplayName } from "./entityDisplayNames";
import { TableTabConfig } from "./tableConfig.types";

// Shared width bands, kept consistent across every table here.
const WIDTH_SHORT = 150;
const WIDTH_MEDIUM = 250;
const WIDTH_LONG = 400;
const WIDTH_DATE = 250;

/**
 * Contact's related tables, as config rather than as code. Tab ids are the keys the old
 * table configs used, because they name the search and page-size inputs.
 */
const contactRelatedTables: TableTabConfig[] = [
  {
    id: "static-marketing-lists",
    label: "Static Marketing Lists",
    entityName: "listmember",
    // By list name only - the other columns are a flag, a date and a count.
    searchable: true,
    searchFields: ["listname"],
    sort: { primary: { key: "listname", dir: "asc" } },
    columns: [
      {
        key: "listname",
        label: "Name",
        width: WIDTH_LONG,
        sortable: true,
        link: { to: "/static-marketing-list", idKey: "listid", newTab: "button" },
      },
      {
        key: "g4c_issubscription",
        label: "Is Subscription",
        width: WIDTH_MEDIUM,
        sortable: true,
        display: "yesno",
      },
      {
        // When the contact joined the list, not when the list itself was created.
        key: "dateadded",
        label: "Date Added",
        width: WIDTH_DATE,
        sortable: true,
        display: "datetime",
      },
      {
        key: "membercount",
        label: "Member Count",
        sortable: true,
        display: "number",
      },
    ],
  },
  {
    id: "other-addresses",
    label: "Other Addresses",
    entityName: "customeraddress",
    sort: { primary: { key: "addressnumber", dir: "asc" } },
    // The primary address is shown on the form itself, not in this table.
    exclude: { key: "addressnumber", equals: "1" },
    columns: [
      {
        key: "addressnumber",
        label: "Address Number",
        width: WIDTH_MEDIUM,
        sortable: true,
      },
      { key: "line1", label: "Address Line 1", width: WIDTH_MEDIUM, sortable: true },
      { key: "line2", label: "Address Line 2", width: WIDTH_MEDIUM, sortable: true },
      { key: "line3", label: "Address Line 3", width: WIDTH_MEDIUM, sortable: true },
      { key: "city", label: "City", width: WIDTH_SHORT, sortable: true },
      { key: "county", label: "County", width: WIDTH_SHORT, sortable: true },
      { key: "country", label: "Country", width: WIDTH_SHORT, sortable: true },
      {
        key: "postalcode",
        label: "Postal Code",
        width: WIDTH_SHORT,
        sortable: true,
      },
      {
        key: "createdon",
        label: "Created On",
        width: WIDTH_DATE,
        sortable: true,
        display: "datetime",
      },
      {
        key: "modifiedon",
        label: "Modified On",
        sortable: true,
        display: "datetime",
      },
    ],
  },
  {
    id: "communication-activities",
    label: "Communication Activities",
    entityName: "g4c_communicationactivity",
    searchable: true,
    sort: { primary: { key: "scheduledstart", dir: "desc" } },
    columns: [
      {
        key: "scheduledstart",
        label: "Scheduled Start",
        width: WIDTH_DATE,
        sortable: true,
        display: "datetime",
        link: { to: "/communication-activity", idKey: "id", newTab: "button" },
      },
      {
        key: "g4c_communicationname",
        label: "Communication",
        width: WIDTH_LONG,
        sortable: true,
        link: {
          to: "/email-communication",
          idKey: "g4c_communication",
          newTab: "button",
        },
      },
      {
        key: "g4c_enddate",
        label: "Communication End Date",
        width: WIDTH_DATE,
        sortable: true,
        display: "datetime",
      },
      {
        key: "g4c_test",
        label: "Test",
        width: WIDTH_SHORT,
        sortable: true,
        display: "yesno",
      },
      {
        key: "statecode",
        label: "Activity Status",
        width: WIDTH_SHORT,
        sortable: true,
        display: "component",
        componentKey: "communicationActivityStatus",
      },
      {
        key: "g4ca_trigger",
        label: "Trigger",
        width: WIDTH_MEDIUM,
        sortable: true,
      },
      {
        key: "g4ca_canceltrigger",
        label: "Cancel Trigger",
        width: WIDTH_MEDIUM,
        sortable: true,
      },
      {
        key: "createdon",
        label: "Created On",
        width: WIDTH_DATE,
        sortable: true,
        display: "datetime",
      },
      {
        key: "modifiedon",
        label: "Modified On",
        sortable: true,
        display: "datetime",
      },
    ],
  },
  {
    id: "communication-audits",
    label: "Communication Audits",
    entityName: "g4c_communicationaudit",
    searchable: true,
    sort: { primary: { key: "g4c_auditdate", dir: "desc" } },
    columns: [
      {
        key: "name",
        label: "Name",
        width: WIDTH_MEDIUM,
        sortable: true,
        link: { to: "/communication-audit", idKey: "id", newTab: "button" },
      },
      {
        key: "g4c_communicationidname",
        label: "Communication",
        width: WIDTH_LONG,
        sortable: true,
        link: {
          to: "/email-communication",
          idKey: "g4c_communicationid",
          newTab: "button",
        },
      },
      { key: "g4c_page", label: "Page", width: WIDTH_LONG, sortable: true },
      {
        key: "g4c_auditdate",
        label: "Audit Date",
        sortable: true,
        display: "datetime",
      },
    ],
  },
  {
    id: "contact-audits",
    label: "Contact Audits",
    entityName: "g4gdpr_contactaudit",
    sort: { primary: { key: "createdon", dir: "desc" } },
    columns: [
      {
        key: "createdon",
        label: "Created On",
        width: WIDTH_DATE,
        sortable: true,
        display: "datetime",
        link: { to: "/contact-audit", idKey: "id", newTab: "button" },
      },
      {
        key: "g4gdpr_donotbulkemail",
        label: "Club Email",
        width: WIDTH_MEDIUM,
        sortable: true,
        display: "yesno",
      },
      {
        key: "g4gdpr_g4c_donotallowsms",
        label: "Club SMS",
        width: WIDTH_MEDIUM,
        sortable: true,
        display: "yesno",
      },
      {
        key: "g4gdpr_g4_donotbulkemail3rdparty",
        label: "Partner Email",
        width: WIDTH_MEDIUM,
        sortable: true,
        display: "yesno",
      },
      {
        key: "g4gdpr_g4_donotsms3rdparty",
        label: "Partner SMS",
        width: WIDTH_MEDIUM,
        sortable: true,
        display: "yesno",
      },
    ],
  },
  {
    id: "subscription-audits",
    label: "Subscription Audits",
    entityName: "g4gdpr_subscriptionaudit",
    sort: { primary: { key: "createdon", dir: "desc" } },
    columns: [
      {
        key: "g4gdpr_marketinglistname",
        label: "List Name",
        width: WIDTH_LONG,
        sortable: true,
      },
      {
        key: "g4gdpr_status",
        label: "Status",
        width: WIDTH_SHORT,
        sortable: true,
      },
      {
        key: "createdon",
        label: "Created On",
        sortable: true,
        display: "datetime",
      },
    ],
  },
];

/** Account's related tables: the contacts that belong to it. */
const accountRelatedTables: TableTabConfig[] = [
  {
    id: "account-contacts",
    label: "Contacts",
    entityName: "contact",
    searchable: true,
    searchFields: ["fullname", "emailaddress1"],
    sort: { primary: { key: "fullname", dir: "asc" } },
    columns: [
      {
        key: "fullname",
        label: "Name",
        width: WIDTH_MEDIUM,
        sortable: true,
        link: { to: "/contact", idKey: "id", newTab: "button" },
      },
      {
        key: "emailaddress1",
        label: "Email Address",
        width: WIDTH_LONG,
        sortable: true,
      },
      {
        key: "createdon",
        label: "Created On",
        width: WIDTH_DATE,
        sortable: true,
        display: "datetime",
      },
      {
        key: "modifiedon",
        label: "Modified On",
        width: WIDTH_DATE,
        sortable: true,
        display: "datetime",
      },
    ],
  },
];

/**
 * The related tables a page can show, and how each one looks by default - its label,
 * columns, sort and paging. Keyed by the entity whose page it is.
 *
 * The one definition of these: the shipped form renders this list directly, the designer
 * offers it as the sources a tab can point at, and a tab created in the designer is
 * seeded from it. An author and the shipped page cannot disagree about what a related
 * table looks like.
 */
export const defaultRelatedTables: Record<string, TableTabConfig[]> = {
  account: accountRelatedTables,
  contact: contactRelatedTables,
};

/** The related tables on offer for an entity's page; none for an entity with no defaults. */
export function getDefaultRelatedTables(entityName: string | undefined) {
  return (entityName && defaultRelatedTables[entityName]) || [];
}

/**
 * What a related table is called on screen: the shipped tab's own wording where the
 * entity has a default, so the designer's source list reads the same as the form does,
 * and the entity's display name only where it has none.
 */
export function relatedTableLabel(
  parentEntity: string | undefined,
  entityName: string
) {
  const found = getDefaultRelatedTables(parentEntity).find(
    (tab) => tab.entityName === entityName
  );

  return found?.label ?? getEntityDisplayName(entityName);
}

/**
 * A new tab on a source, carrying that source's defaults where there are any. The id is
 * the caller's, since the ids stored here belong to the shipped tabs. A source with no
 * defaults gets what a new tab has always got - a name and nothing else.
 */
export function defaultTableTab(
  parentEntity: string | undefined,
  entityName: string
): Omit<TableTabConfig, "id"> {
  const found = getDefaultRelatedTables(parentEntity).find(
    (tab) => tab.entityName === entityName
  );

  if (!found) {
    return { label: getEntityDisplayName(entityName), entityName, columns: [] };
  }

  const { id, ...rest } = found;

  return rest;
}
